import { useState } from 'react';
import {
  BarChart3,
  Sliders,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Wrench,
  Calculator,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

interface IPSInput {
  assetId: string;
  operatingTemperatureMin: number;
  operatingTemperatureMax: number;
  thermalCyclingFrequency: number;
  hoursInCUIWindow: number;
  assetType: string;
  hasDeadLegs: boolean;
  hasLowPoints: boolean;
  hasPipeShoes: boolean;
  hasFlanges: boolean;
  insulationComplexity: 'low' | 'medium' | 'high';
  isoCorrosivityClass: string;
  coastalDistance: number;
  coolingTowerDistance: number;
  annualRainfall: number;
  previousCUIRepairs: number;
  adjacentUnitFailures: number;
  historicalCorrosionRate: number;
  yearsSinceLastInspection: number;
  lastInspectionMethod: string;
  lastInspectionCoverage: number;
  yearsSinceLastScreening: number;
}

interface IPSResult {
  assetId: string;
  thermalSusceptibility: number;
  geometricVulnerability: number;
  environmentalAggressiveness: number;
  historicalDegradation: number;
  inspectionConfidence: number;
  overallScore: number;
  priorityCategory: 'critical' | 'high' | 'medium' | 'low';
  evidenceBreakdown: {
    thermalFactors: string[];
    geometricFactors: string[];
    environmentalFactors: string[];
    historicalFactors: string[];
    confidenceFactors: string[];
  };
  recommendedAction: string;
}

const PRESET_CIRCUITS: { name: string; badge: string; input: IPSInput }[] = [
  {
    name: 'Preset 1: Hydrocracker Reactor Feed (CRITICAL RISK)',
    badge: 'CRITICAL',
    input: {
      assetId: '12"-HC-2019-CS',
      operatingTemperatureMin: 65,
      operatingTemperatureMax: 95,
      thermalCyclingFrequency: 18,
      hoursInCUIWindow: 7800,
      assetType: 'piping',
      hasDeadLegs: true,
      hasLowPoints: true,
      hasPipeShoes: true,
      hasFlanges: true,
      insulationComplexity: 'high',
      isoCorrosivityClass: 'C5-M',
      coastalDistance: 1.2,
      coolingTowerDistance: 0.3,
      annualRainfall: 1400,
      previousCUIRepairs: 2,
      adjacentUnitFailures: 1,
      historicalCorrosionRate: 0.35,
      yearsSinceLastInspection: 4,
      lastInspectionMethod: 'visual_only',
      lastInspectionCoverage: 40,
      yearsSinceLastScreening: 4,
    },
  },
  {
    name: 'Preset 2: Coastal Stripper Column Overhead (HIGH RISK)',
    badge: 'HIGH',
    input: {
      assetId: 'TK-402A-SOUR-STRIPPER',
      operatingTemperatureMin: 45,
      operatingTemperatureMax: 70,
      thermalCyclingFrequency: 8,
      hoursInCUIWindow: 5200,
      assetType: 'vessel',
      hasDeadLegs: false,
      hasLowPoints: true,
      hasPipeShoes: false,
      hasFlanges: true,
      insulationComplexity: 'medium',
      isoCorrosivityClass: 'C4',
      coastalDistance: 2.5,
      coolingTowerDistance: 1.2,
      annualRainfall: 950,
      previousCUIRepairs: 1,
      adjacentUnitFailures: 0,
      historicalCorrosionRate: 0.18,
      yearsSinceLastInspection: 3,
      lastInspectionMethod: 'visual_thermal',
      lastInspectionCoverage: 70,
      yearsSinceLastScreening: 2,
    },
  },
  {
    name: 'Preset 3: Superheated Steam Utility Line (LOW RISK)',
    badge: 'LOW',
    input: {
      assetId: '08"-STM-4001-CS',
      operatingTemperatureMin: 180,
      operatingTemperatureMax: 240,
      thermalCyclingFrequency: 2,
      hoursInCUIWindow: 400,
      assetType: 'piping',
      hasDeadLegs: false,
      hasLowPoints: false,
      hasPipeShoes: true,
      hasFlanges: false,
      insulationComplexity: 'low',
      isoCorrosivityClass: 'C2',
      coastalDistance: 25,
      coolingTowerDistance: 5.0,
      annualRainfall: 600,
      previousCUIRepairs: 0,
      adjacentUnitFailures: 0,
      historicalCorrosionRate: 0.04,
      yearsSinceLastInspection: 2,
      lastInspectionMethod: 'pec_full',
      lastInspectionCoverage: 95,
      yearsSinceLastScreening: 1,
    },
  },
];

export function IPSPage() {
  const [input, setInput] = useState<IPSInput>(PRESET_CIRCUITS[0].input);
  const [result, setResult] = useState<IPSResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Deterministic 5-factor IPS algorithm
  const calculateIPS = (inp: IPSInput): IPSResult => {
    // 1. Thermal Susceptibility (0 - 10)
    let thermal = 2.0;
    if (inp.operatingTemperatureMin >= -4 && inp.operatingTemperatureMax <= 120) {
      thermal += 4.5;
    }
    if (inp.operatingTemperatureMin >= 60 && inp.operatingTemperatureMax <= 95) {
      thermal += 2.0; // Peak API RP 583 condensation window
    }
    thermal += Math.min(2.0, (inp.thermalCyclingFrequency / 20) * 2.0);
    thermal = Math.min(10, Math.round(thermal * 10) / 10);

    // 2. Geometric Vulnerability (0 - 10)
    let geom = 1.0;
    if (inp.hasDeadLegs) geom += 2.5;
    if (inp.hasLowPoints) geom += 2.5;
    if (inp.hasPipeShoes) geom += 2.0;
    if (inp.hasFlanges) geom += 1.5;
    if (inp.insulationComplexity === 'high') geom += 1.5;
    geom = Math.min(10, Math.round(geom * 10) / 10);

    // 3. Environmental Aggressiveness (0 - 10)
    let env = 2.0;
    if (inp.isoCorrosivityClass.includes('C5')) env += 4.0;
    else if (inp.isoCorrosivityClass.includes('C4')) env += 3.0;
    else if (inp.isoCorrosivityClass.includes('C3')) env += 1.5;

    if (inp.coastalDistance < 2.0) env += 2.0;
    if (inp.coolingTowerDistance < 0.5) env += 1.5;
    env = Math.min(10, Math.round(env * 10) / 10);

    // 4. Historical Degradation (0 - 10)
    let hist = 1.0;
    hist += Math.min(4.0, inp.previousCUIRepairs * 2.0);
    hist += Math.min(2.0, inp.adjacentUnitFailures * 1.5);
    hist += Math.min(3.0, (inp.historicalCorrosionRate / 0.3) * 3.0);
    hist = Math.min(10, Math.round(hist * 10) / 10);

    // 5. Inspection Confidence (0 - 10, inverted risk: lower confidence = higher penalty)
    let conf = Math.max(1, 10 - inp.yearsSinceLastInspection * 1.8);
    if (inp.lastInspectionMethod === 'pec_full') conf += 2.0;
    conf = Math.min(10, Math.round(conf * 10) / 10);

    // Overall Weighted Score
    const overall =
      thermal * 0.30 +
      geom * 0.25 +
      env * 0.15 +
      hist * 0.20 +
      (10 - conf) * 0.10;
    const roundedOverall = Math.round(overall * 10) / 10;

    let priorityCategory: 'critical' | 'high' | 'medium' | 'low' = 'low';
    if (roundedOverall >= 7.5) priorityCategory = 'critical';
    else if (roundedOverall >= 5.5) priorityCategory = 'high';
    else if (roundedOverall >= 3.5) priorityCategory = 'medium';
    else priorityCategory = 'low';

    return {
      assetId: inp.assetId,
      thermalSusceptibility: thermal,
      geometricVulnerability: geom,
      environmentalAggressiveness: env,
      historicalDegradation: hist,
      inspectionConfidence: conf,
      overallScore: roundedOverall,
      priorityCategory,
      evidenceBreakdown: {
        thermalFactors: [
          `Operating range ${inp.operatingTemperatureMin}°C - ${inp.operatingTemperatureMax}°C within API RP 583 zone.`,
          `High thermal cycling (${inp.thermalCyclingFrequency} cycles/year) inducing jacket breathing.`,
        ],
        geometricFactors: [
          inp.hasDeadLegs ? 'Dead leg present without convective heat circulation.' : 'No dead legs recorded.',
          inp.hasLowPoints ? 'Low point water collection trap identified.' : 'No low point traps.',
          inp.hasPipeShoes ? 'Pipe support saddle trapping pooling water.' : 'No pipe shoe traps.',
        ],
        environmentalFactors: [
          `ISO 12944 Corrosivity class ${inp.isoCorrosivityClass}.`,
          `Marine coastal distance ${inp.coastalDistance} km with aerosol chloride deposition.`,
        ],
        historicalFactors: [
          `${inp.previousCUIRepairs} documented historical CUI repair events on this circuit.`,
          `Recorded localized corrosion rate of ${inp.historicalCorrosionRate} mm/yr.`,
        ],
        confidenceFactors: [
          `Last inspection conducted ${inp.yearsSinceLastInspection} years ago via ${inp.lastInspectionMethod}.`,
          `Coverage factor: ${inp.lastInspectionCoverage}%.`,
        ],
      },
      recommendedAction:
        roundedOverall >= 7.5
          ? 'Mandatory immediate NDT screening via Pulsed Eddy Current (PEC) or targeted insulation strip within 30 days.'
          : roundedOverall >= 5.5
          ? 'Schedule for drone infrared thermography and ultrasonic thickness screening during upcoming quarter.'
          : 'Maintain baseline 36-month RBI monitoring interval.',
    };
  };

  const handleCalculate = async () => {
    setIsLoading(true);
    try {
      try {
        const response = await fetch('/api/v1/ips/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        if (response.ok) {
          const data = await response.json();
          setResult(data.data);
          toast.success('Inspection Priority Score calculated');
          setIsLoading(false);
          return;
        }
      } catch {
        // Fallback
      }

      await new Promise((r) => setTimeout(r, 400));
      const res = calculateIPS(input);
      setResult(res);
      toast.success('IPS Risk Prioritization completed');
    } catch {
      toast.error('Failed to calculate IPS');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPreset = (presetInput: IPSInput) => {
    setInput(presetInput);
    const res = calculateIPS(presetInput);
    setResult(res);
    toast.success(`Loaded preset: ${presetInput.assetId}`);
  };

  const getPriorityBadge = (category: string) => {
    switch (category) {
      case 'critical':
        return (
          <span className="badge badge-danger text-sm py-1 px-3 flex items-center gap-1.5 font-bold">
            <XCircle className="h-4 w-4" /> CRITICAL INTERVENTION (P1)
          </span>
        );
      case 'high':
        return (
          <span className="badge badge-warning text-sm py-1 px-3 flex items-center gap-1.5 font-bold">
            <AlertTriangle className="h-4 w-4" /> HIGH PRIORITY (P2)
          </span>
        );
      case 'medium':
        return (
          <span className="badge badge-info text-sm py-1 px-3 flex items-center gap-1.5 font-bold">
            <CheckCircle2 className="h-4 w-4" /> MEDIUM PRIORITY (P3)
          </span>
        );
      case 'low':
        return (
          <span className="badge badge-success text-sm py-1 px-3 flex items-center gap-1.5 font-bold">
            <CheckCircle2 className="h-4 w-4" /> LOW RISK (P4)
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="telemetry-pill bg-purple-50 text-purple-700 font-bold border border-purple-200">
                RISK-BASED INSPECTION // API 581 COMPLIANT
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
              <BarChart3 className="h-7 w-7 text-purple-600" />
              Inspection Priority Score (IPS)
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Deterministic multi-factor risk prioritization synthesizing API RP 583 temperature windows, geometric traps, coastal salinity, and historical records.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/tools/ffs" className="btn btn-secondary text-xs sm:text-sm py-2 px-3 shadow-sm">
              <Calculator className="h-4 w-4 text-blue-600" />
              <span>Evaluate FFS</span>
            </Link>
            <Link to="/cmms" className="btn btn-primary text-xs sm:text-sm py-2 px-3 shadow-sm">
              <Wrench className="h-4 w-4" />
              <span>Dispatch to SAP PM</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Preset Circuits Bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs font-mono font-bold text-slate-700">
          <Sliders className="h-4 w-4 text-purple-600" />
          <span>SELECT CIRCUIT PRESET:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESET_CIRCUITS.map((p, idx) => (
            <button
              key={idx}
              onClick={() => handleSelectPreset(p.input)}
              className={`btn text-xs py-1.5 px-3 rounded-lg font-medium transition-all ${
                input.assetId === p.input.assetId
                  ? 'btn-primary shadow-sm'
                  : 'btn-secondary text-slate-700 hover:bg-white'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Form Inputs */}
        <div className="lg:col-span-6 card bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="card-header border-b border-slate-100 p-5 flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900">Hazard Input Variables</h2>
            <span className="telemetry-pill bg-slate-100 text-slate-600 font-mono">5 HAZARD CATEGORIES</span>
          </div>
          <div className="card-body p-5 space-y-4">
            <div>
              <label className="label">Circuit / Equipment Tag</label>
              <input
                type="text"
                className="input font-mono font-bold"
                value={input.assetId}
                onChange={(e) => setInput({ ...input, assetId: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Op Temp Min (°C)</label>
                <input
                  type="number"
                  className="input font-mono"
                  value={input.operatingTemperatureMin}
                  onChange={(e) => setInput({ ...input, operatingTemperatureMin: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="label">Op Temp Max (°C)</label>
                <input
                  type="number"
                  className="input font-mono"
                  value={input.operatingTemperatureMax}
                  onChange={(e) => setInput({ ...input, operatingTemperatureMax: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Geometric Checkboxes */}
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
              <span className="text-xs font-bold text-slate-800 uppercase block mb-1">
                Geometric Moisture Traps
              </span>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-700">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={input.hasDeadLegs}
                    onChange={(e) => setInput({ ...input, hasDeadLegs: e.target.checked })}
                    className="accent-purple-600"
                  />
                  <span>Dead Legs / Stagnant Line</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={input.hasLowPoints}
                    onChange={(e) => setInput({ ...input, hasLowPoints: e.target.checked })}
                    className="accent-purple-600"
                  />
                  <span>Piping Low Points / Drains</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={input.hasPipeShoes}
                    onChange={(e) => setInput({ ...input, hasPipeShoes: e.target.checked })}
                    className="accent-purple-600"
                  />
                  <span>Pipe Support Shoes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={input.hasFlanges}
                    onChange={(e) => setInput({ ...input, hasFlanges: e.target.checked })}
                    className="accent-purple-600"
                  />
                  <span>Flanged Connections</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Corrosivity Class</label>
                <select
                  value={input.isoCorrosivityClass}
                  onChange={(e) => setInput({ ...input, isoCorrosivityClass: e.target.value })}
                  className="input font-mono"
                >
                  <option value="C2">C2 - Low (Inland Rural)</option>
                  <option value="C3">C3 - Medium (Urban)</option>
                  <option value="C4">C4 - High (Industrial)</option>
                  <option value="C5-M">C5-M - Very High (Marine Offshore)</option>
                </select>
              </div>
              <div>
                <label className="label">Coastal Distance (km)</label>
                <input
                  type="number"
                  step="0.1"
                  className="input font-mono"
                  value={input.coastalDistance}
                  onChange={(e) => setInput({ ...input, coastalDistance: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <button
              onClick={handleCalculate}
              disabled={isLoading}
              className="btn btn-primary w-full py-3 text-sm font-semibold shadow-md mt-2"
            >
              {isLoading ? 'Synthesizing Risk Matrices...' : 'Calculate Inspection Priority Score'}
            </button>
          </div>
        </div>

        {/* Results & Score Breakdown */}
        <div className="lg:col-span-6 space-y-6">
          <div className="card bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h2 className="text-base font-bold text-slate-900">Priority Score & Recommendation</h2>
                <p className="text-xs text-slate-500 font-mono">ASSET: {input.assetId}</p>
              </div>
              {result && getPriorityBadge(result.priorityCategory)}
            </div>

            {result ? (
              <div className="space-y-5">
                {/* Master Score Display */}
                <div className="p-4 rounded-xl bg-slate-900 text-white flex items-center justify-between">
                  <div>
                    <div className="text-xs font-mono text-slate-400">OVERALL COMPOSITE RISK SCORE</div>
                    <div className="text-4xl font-extrabold font-mono text-white mt-1">
                      {result.overallScore.toFixed(1)} <span className="text-lg font-sans font-normal text-slate-400">/ 10.0</span>
                    </div>
                  </div>
                  <div className="text-right font-mono text-xs text-slate-300">
                    <div>ACTION WINDOW:</div>
                    <strong className="text-amber-400">
                      {result.priorityCategory === 'critical' ? '< 30 DAYS' : result.priorityCategory === 'high' ? '< 90 DAYS' : 'SCHEDULED TURNAROUND'}
                    </strong>
                  </div>
                </div>

                {/* 3D Piping Digital Twin Wireframe */}
                <div className="relative rounded-xl overflow-hidden border border-slate-700 bg-slate-950 shadow-md">
                  <img
                    src="/piping_isometric.jpeg"
                    alt="Piping Circuit Isometric Digital Twin"
                    className="w-full h-44 object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-slate-950/40 flex flex-col justify-between p-3 font-mono text-xs">
                    <div className="flex justify-between items-center">
                      <span className="telemetry-pill bg-purple-900/80 text-purple-300 border border-purple-700">
                        CIRCUIT WIREFRAME // REFINERY-DT-7734
                      </span>
                      <span className="text-[10px] text-slate-300 bg-slate-900/80 px-1.5 py-0.5 rounded">
                        API 581 RBI MAPPING
                      </span>
                    </div>
                    <div className="flex justify-between items-end text-[11px] text-slate-300">
                      <span>FLOW: 1450 m³/h | 68 bar</span>
                      <span className="text-amber-400 font-bold">CRITICAL LOW POINTS HIGHLIGHTED</span>
                    </div>
                  </div>
                </div>

                {/* 5 Hazard Category Bars */}
                <div className="space-y-3 font-mono text-xs">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-600">Thermal Susceptibility (30%)</span>
                      <strong className="text-slate-900">{result.thermalSusceptibility} / 10</strong>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${result.thermalSusceptibility * 10}%` }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-600">Geometric Vulnerability (25%)</span>
                      <strong className="text-slate-900">{result.geometricVulnerability} / 10</strong>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500 rounded-full" style={{ width: `${result.geometricVulnerability * 10}%` }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-600">Environmental Aggressiveness (15%)</span>
                      <strong className="text-slate-900">{result.environmentalAggressiveness} / 10</strong>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${result.environmentalAggressiveness * 10}%` }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-600">Historical Degradation (20%)</span>
                      <strong className="text-slate-900">{result.historicalDegradation} / 10</strong>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: `${result.historicalDegradation * 10}%` }} />
                    </div>
                  </div>
                </div>

                {/* Recommendation Box */}
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs space-y-2">
                  <div className="font-bold text-slate-900">Prescribed Inspection Action</div>
                  <p className="text-slate-600 font-sans leading-relaxed">
                    {result.recommendedAction}
                  </p>
                </div>

                <div className="pt-2 flex gap-3">
                  <Link to="/cmms" className="btn btn-primary flex-1 py-2.5 text-xs font-mono font-semibold">
                    <Wrench className="h-4 w-4" />
                    <span>Create CMMS Work Order</span>
                  </Link>
                  <Link to="/tools/ffs" className="btn btn-secondary flex-1 py-2.5 text-xs font-mono font-semibold">
                    <Calculator className="h-4 w-4 text-blue-600" />
                    <span>Calculate FFS</span>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-72 text-slate-400">
                <BarChart3 className="h-12 w-12 mb-3 text-slate-300" />
                <p className="text-base font-semibold text-slate-700">Enter Parameters & Run Risk Scoring</p>
                <p className="text-xs text-slate-400 mt-1">Or load one of the calibrated circuit presets above</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
